/* eslint-disable no-param-reassign */
/* eslint-disable no-prototype-builtins */
import * as X from 'vuex';

/* Wrappers used to get corŕect type information in actions when building a module */

type MutationArgument<M extends X.Mutation<any>> =
  M extends ((state: any) => any) ? undefined
  : M extends ((state: any, payload: infer P) => any) ? P
  : undefined;
type MutationPayload<M extends X.MutationTree<any>, K extends keyof M> =
  { type: K } & (
    MutationArgument<M[K]> extends undefined ? { }
    : MutationArgument<M[K]>
  );
type MutationType<M extends X.MutationTree<any>, K extends keyof M> =
  [ MutationPayload<M, K> ]
  | [ MutationPayload<M, K>, X.CommitOptions ]
  | (
    MutationArgument<M[K]> extends undefined ? ([K] | [K, undefined, X.CommitOptions])
    : ([K, MutationArgument<M[K]>] | [K, MutationArgument<M[K]>, X.CommitOptions])
  );
type Commit<M extends X.MutationTree<any>> = <K extends keyof M>(...args: MutationType<M, K>) => void;

type Getter<S, R, G extends X.GetterTree<S, R>>
  = (state: S, getters: Getters<G>, rootState: R, rootGetters: any) => any;
interface GetterTree<S, R, G extends X.GetterTree<S, R>> {
  [key: string]: Getter<S, R, G>;
}

type ReturnType<T> = T extends (...args: any[]) => infer R ? R : any;
type Getters<G extends X.GetterTree<any, any>> = { readonly [K in keyof G]: ReturnType<G[K]> };

interface ActionTree<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>> {
  [key: string]: Action<S, R, M, G>;
}

interface ActionContext<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>> {
  dispatch: X.Dispatch;
  commit: Commit<M>;
  state: S;
  getters: Getters<G>;
  rootState: R;
  rootGetters: any;
}

type ActionHandler<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<any, any>> =
  (this: undefined, injectee: ActionContext<S, R, M, G>, payload: any) => any;
interface ActionObject<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<any, any>> {
  root?: boolean;
  handler: ActionHandler<S, R, M, G>;
}

type Action<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<any, any>>
  = ActionHandler<S, R, M, G> | ActionObject<S, R, M, G>;

/* builder types */

interface StateBuilder {
  state<S>(state: S | (() => S)): RootBuilder<S> & MutationBuilder<S, {}>;
}
interface RootBuilder<S> {
  root<R>(): MutationBuilder<S, R>;
}
interface MutationBuilder<S, R> {
  mutations<M extends X.MutationTree<S>>(t: M):
    GetterBuilder<S, R, M, {}> & ActionBuilder<S, R, M, {}> & ModuleBuilder<S, R, M, {}, {}>;
  defaultMutations<M extends X.MutationTree<S>>(t?: M):
    GetterBuilder<S, R, M & DefaultMutations<S>, {}>
    & ActionBuilder<S, R, M & DefaultMutations<S>, {}>
    & ModuleBuilder<S, R, M & DefaultMutations<S>, {}, {}>;
}
type DefaultMutations<S> = { [K in keyof S]: (state: S, payload: S[K]) => void };
function defaultMutations<S extends object>(state: S): DefaultMutations<S> {
  const result = {} as DefaultMutations<S>;
  for (const key in state) {
    if (state.hasOwnProperty(key)) {
      result[key] = (s, payload) => { s[key] = payload; };
    }
  }
  return result;
}
interface GetterBuilder<S, R, M extends X.MutationTree<S>, GG extends GetterTree<S, R, any>> {
  getters<G extends GetterTree<S, R, GG>>(t: G):
    GetterBuilder<S, R, M, G & GG> & ActionBuilder<S, R, M, G & GG> & ModuleBuilder<S, R, M, G & GG, {}>;
}
interface ActionBuilder<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>> {
  actions<A extends ActionTree<S, R, M, G>>(t: A): ModuleBuilder<S, R, M, G, A>;
}
interface ModuleBuilder<S, R, M extends X.MutationTree<S>,
    G extends X.GetterTree<S, R>, A extends ActionTree<S, R, M, G>> {
  hideMutations(): ModuleBuilder<S, R, {}, G, A>;
  register(store: X.Store<R>, path: string): ModuleWrapper<S, R, M, G, A>;
  createStore(): StoreWrapper<S, M, G, A>;
}

/* wrapper types for type safe access to the module */

type MutationWrapper<M extends X.Mutation<any>> =
  MutationArgument<M> extends undefined ? () => void : (arg: MutationArgument<M>) => void;
type MutationTreeWrapper<MT extends X.MutationTree<any>> = { readonly [K in keyof MT]: MutationWrapper<MT[K]> };
function mutationWrapper<M extends X.Mutation<any>>(store: X.Store<any>, name: string): MutationWrapper<M> {
  return ((arg: MutationArgument<M>) => store.commit(name, arg)) as MutationWrapper<M>;
}
function mutationTreeWrapper<MT extends X.MutationTree<any>>(
  store: X.Store<any>, namespace: string, mutations: MT,
):
      MutationTreeWrapper<MT> {
  const result: any = {};
  for (const mutationName in mutations) {
    if (mutations.hasOwnProperty(mutationName)) {
      result[mutationName] = mutationWrapper(store, (namespace ? `${namespace}/` : '') + mutationName);
    }
  }
  return result;
}

type HandlerOf<A extends Action<any, any, any, any>> =
  A extends ActionHandler<any, any, any, any> ? A : A extends ActionObject<any, any, any, any> ? A['handler'] : never;
type ActionArgument<A extends Action<any, any, any, any>> =
  HandlerOf<A> extends (first: any) => any ? undefined
  : HandlerOf<A> extends (first: any, payload: infer Arg) => any ? Arg : undefined;
type AsPromise<A> = A extends Promise<any> ? A : Promise<A>;
type ActionResult<A extends Action<any, any, any, any>> = AsPromise<ReturnType<HandlerOf<A>>>;
type ActionWrapper<A extends Action<any, any, any, any>> =
  ActionArgument<A> extends undefined
  ? () => ActionResult<A>
  : (arg: ActionArgument<A>) => ActionResult<A>;
type ActionTreeWrapper<A extends ActionTree<any, any, any, any>> = { readonly [K in keyof A]: ActionWrapper<A[K]> };
function actionWrapper<A extends Action<any, any, any, any>>(store: X.Store<any>, name: string): ActionWrapper<A> {
  return ((...args: any[]) => store.dispatch(name, args[0])) as ActionWrapper<A>;
}
function actionTreeWrapper<AT extends ActionTree<any, any, any, any>>(
  store: X.Store<any>, namespace: string, actions: AT,
): ActionTreeWrapper<AT> {
  const result: any = {};
  for (const actionName in actions) {
    if (actions.hasOwnProperty(actionName)) {
      result[actionName] = actionWrapper(store, (namespace ? `${namespace}/` : '') + actionName);
    }
  }
  return result as ActionTreeWrapper<AT>;
}

type GetterTreeWrapper<G extends X.GetterTree<any, any>> = { readonly [K in keyof G]: ReturnType<G[K]> };
function getterTreeWrapper<G extends X.GetterTree<any, any>>(
  store: X.Store<any>, namespace: string, getters: G,
): GetterTreeWrapper<G> {
  const result: any = {};
  for (const p in getters) {
    if (getters.hasOwnProperty(p)) {
      Object.defineProperty(result, p, {
        get() { return store.getters[(namespace ? `${namespace}/` : '') + p]; },
      });
    }
  }
  return result as GetterTreeWrapper<G>;
}

interface ActionContextWrapper<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>> {
  readonly state: DeepReadonly<S>;
  readonly commit: MutationTreeWrapper<M>;
  readonly getters: GetterTreeWrapper<G>;
}
export interface ModuleWrapper<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>,
    A extends ActionTree<S, R, M, G>> extends ActionContextWrapper<S, R, M, G> {
  readonly dispatch: ActionTreeWrapper<A>;
  (ctx: { rootState: any } & Partial<ActionContext<any, any, any, any> | X.ActionContext<any, any>>):
    ActionContextWrapper<S, R, M, G>;
}
export interface StoreWrapper<S, M extends X.MutationTree<S>, G extends X.GetterTree<S, any>,
    A extends ActionTree<S, any, M, G>> extends ActionContextWrapper<S, any, M, G> {
  readonly dispatch: ActionTreeWrapper<A>;
  readonly store: X.Store<S>;
}
function moduleWrapper<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>,
    A extends ActionTree<S, R, M, G>>(
  store: X.Store<any>, namespace: string, s: S, m: M, g: G, a: A,
):
      ModuleWrapper<S, R, M, G, A> {
  const result: any = acWrapper<S, R, M, G>(namespace, m, g);
  result.state = s;
  result.commit = mutationTreeWrapper(store, namespace, m);
  result.getters = getterTreeWrapper(store, namespace, g);
  result.dispatch = actionTreeWrapper(store, namespace, a);
  return result;
}
function acWrapper<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>>(
  namespace: string, m: M, g: G,
):
      (ctx: ActionContext<S, R, M, G>) => ActionContextWrapper<S, R, M, G> {
  return (ctx) => ({
    state: ((ctx.rootState ? ctx.rootState : {}) as any)[namespace] as DeepReadonly<S>,
    commit: acMutationWrapper<S, M>(namespace, ctx.commit, m),
    getters: acGetterWrapper<S, G>(namespace, ctx.rootGetters, g),
  });
}
function acMutationWrapper<S, M extends X.MutationTree<S>>(
  namespace: string, commit: Commit<any>, m: M,
): MutationTreeWrapper<M> {
  return new Proxy(((commit || {}) as unknown) as MutationTreeWrapper<M>, {
    get(target, name) {
      if (m.hasOwnProperty(name)) {
        return (arg: any) => commit((namespace ? `${namespace}/` : '') + name.toString(), arg, { root: true });
      }
      return (target as any)[name];
    },
  });
}
function acGetterWrapper<S, G extends X.GetterTree<S, any>>(
  namespace: string, rootGetters: any, g: G,
): GetterTreeWrapper<G> {
  return new Proxy(((rootGetters || {}) as unknown) as GetterTreeWrapper<G>, {
    get(target, name) {
      if (g.hasOwnProperty(name)) {
        return rootGetters[(namespace ? `${namespace}/` : '') + name.toString()];
      }
      return (target as any)[name];
    },
  });
}
export default function moduleBuilder() {
  let s: any;
  let m: any;
  let g: any;
  let a: any;
  const builder: any = {
    state(t: any) { s = t; return this; },
    root() { return this; },
    mutations(t: any) { m = t; return this; },
    defaultMutations(t: any = {}) { m = { ...defaultMutations(s), ...t }; return this; },
    getters(t: any) { g = g === undefined ? t : { ...t, ...g }; return this; },
    actions(t: any) { a = t; return this; },
    hideMutations() { return this; },
    register(store: X.Store<any>, path: string) {
      return registerModule(store, path, {
        namespaced: true,
        state: s,
        mutations: m,
        getters: g,
        actions: a,
      });
    },
    createStore() {
      return createStore({
        namespaced: true,
        state: s,
        mutations: m,
        getters: g,
        actions: a,
      });
    },
  };
  return builder as StateBuilder;
}

interface Module<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>,
    A extends ActionTree<S, R, M, G>> {
  namespaced: true;
  state: S;
  mutations: M;
  getters: G;
  actions: A;
}
export function registerModule<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>,
    A extends ActionTree<S, R, M, G>>(
  store: X.Store<any>, path: string, module: Module<S, R, M, G, A>,
):
      ModuleWrapper<S, R, M, G, A> {
  store.registerModule([path], module as unknown as X.Module<any, any>);
  return moduleWrapper(store, path, module.state, module.mutations, module.getters, module.actions);
}
export function createStore<
  S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>,
  A extends ActionTree<S, R, M, G>
>(
  module: Module<S, R, M, G, A>,
): ModuleWrapper<S, R, M, G, A> & { store: X.Store<R> } {
  const store = new X.Store(module as any);
  const wrapper: any = moduleWrapper(
    store, '', module.state, module.mutations, module.getters as any, module.actions as any,
  );
  wrapper.store = store;
  return wrapper;
}

export function flatModule<S, R, M extends X.MutationTree<S>, G extends X.GetterTree<S, R>,
    A extends ActionTree<S, R, M, G>, F>(module: ModuleWrapper<S, R, M, G, A>, extraFunctions: F = {} as F) {
  return extractProperties(extractProperties({
    ...extraFunctions,
    ...module.commit,
    ...module.dispatch,
  }, module.state), module.getters);
}

function extractProperties<T, G>(mutable: T, g: G): T & G {
  Object.getOwnPropertyNames(g).forEach((k) => {
    const descriptor = Object.getOwnPropertyDescriptor(g, k);
    if (descriptor && typeof descriptor.get === 'function') {
      Object.defineProperty(mutable, k, {
        get: descriptor.get,
      });
    }
  });
  return mutable as T & G;
}
